import { useEffect, useState } from 'react';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function apiHeaders(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}


async function readResponse(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  if (!text) {
    return null;
  }

  return JSON.parse(text);
}


async function loadStructuredForm(
  typeId
) {
  const encoded =
    encodeURIComponent(typeId);

  const [
    sectionsResponse,
    fieldsResponse
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/assignment_type_structured_sections?assignment_type_id=eq.${encoded}&select=*&order=section_order.asc`,
      {
        headers:
          apiHeaders()
      }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/assignment_type_structured_fields?assignment_type_id=eq.${encoded}&select=*&order=field_order.asc`,
      {
        headers:
          apiHeaders()
      }
    )
  ]);

  const sections =
    await readResponse(
      sectionsResponse,
      'Loading Structured Form sections'
    );

  const fields =
    await readResponse(
      fieldsResponse,
      'Loading Structured Form questions'
    );

  const fieldRows =
    Array.isArray(fields)
      ? fields
      : [];

  return (
    Array.isArray(sections)
      ? sections
      : []
  ).map(section => ({
    ...section,
    fields:
      fieldRows
        .filter(
          field =>
            field.section_id ===
            section.id
        )
        .sort(
          (a, b) =>
            Number(
              a.field_order || 0
            ) -
            Number(
              b.field_order || 0
            )
        )
  }));
}


async function saveStructuredForm(
  typeId,
  sections,
  cmsToken
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/assignment_library_save_structured_form`,
      {
        method: 'POST',
        headers:
          apiHeaders({
            'Content-Type':
              'application/json'
          }),
        body: JSON.stringify({
          p_cms_token:
            cmsToken,
          p_type_id:
            typeId,
          p_sections:
            sections
        })
      }
    );

  const result =
    await readResponse(
      response,
      'Saving Structured Form'
    );

  if (result?.ok === false) {
    throw new Error(
      result.message ||
        'Saving Structured Form failed.'
    );
  }

  return result;
}



// UNIFIED_STRUCTURED_FORM_BUILDER_V1

async function loadCourseStructuredForm(
  courseAssignmentId
) {
  const id =
    encodeURIComponent(
      courseAssignmentId
    );

  const [
    sectionsResponse,
    fieldsResponse
  ] = await Promise.all([
    fetch(
      SUPABASE_URL +
      '/rest/v1/course_structured_sections' +
      '?course_assignment_id=eq.' +
      id +
      '&select=id,title,description,section_order' +
      '&order=section_order.asc',
      {
        headers:
          apiHeaders()
      }
    ),

    fetch(
      SUPABASE_URL +
      '/rest/v1/course_structured_fields' +
      '?course_assignment_id=eq.' +
      id +
      '&select=id,course_section_id,label,prompt,field_type,required,options,field_order,word_guidance_min,word_guidance_max' +
      '&order=field_order.asc',
      {
        headers:
          apiHeaders()
      }
    )
  ]);

  const sections =
    await readResponse(
      sectionsResponse,
      'Loading Structured Form sections'
    );

  const fields =
    await readResponse(
      fieldsResponse,
      'Loading Structured Form questions'
    );

  const fieldRows =
    Array.isArray(fields)
      ? fields
      : [];

  return (
    Array.isArray(sections)
      ? sections
      : []
  ).map(section => ({
    ...section,

    fields:
      fieldRows
        .filter(
          field =>
            String(
              field.course_section_id
            ) ===
            String(section.id)
        )
        .sort(
          (a, b) =>
            Number(
              a.field_order || 0
            ) -
            Number(
              b.field_order || 0
            )
        )
  }));
}


function normaliseCourseForSave(
  sections
) {
  return sections.map(
    (
      section,
      sectionIndex
    ) => ({
      id:
        section.id || null,

      title:
        String(
          section.title || ''
        ).trim(),

      description:
        section.description || '',

      section_order:
        sectionIndex + 1,

      fields:
        (
          Array.isArray(
            section.fields
          )
            ? section.fields
            : []
        ).map(
          (
            field,
            fieldIndex
          ) => ({
            id:
              field.id || null,

            label:
              String(
                field.label || ''
              ).trim(),

            prompt:
              field.prompt || '',

            field_type:
              field.field_type ||
              'long_text',

            required:
              field.required !== false,

            options:
              Array.isArray(
                field.options
              )
                ? field.options
                    .map(
                      option =>
                        String(
                          option || ''
                        ).trim()
                    )
                    .filter(Boolean)
                : [],

            field_order:
              fieldIndex + 1,

            word_guidance_min:
              field.word_guidance_min === '' ||
              field.word_guidance_min === null ||
              field.word_guidance_min === undefined
                ? null
                : Number(
                    field.word_guidance_min
                  ),

            word_guidance_max:
              field.word_guidance_max === '' ||
              field.word_guidance_max === null ||
              field.word_guidance_max === undefined
                ? null
                : Number(
                    field.word_guidance_max
                  )
          })
        )
    })
  );
}


async function saveCourseStructuredForm(
  courseAssignmentId,
  sections,
  cmsToken
) {
  const response =
    await fetch(
      SUPABASE_URL +
      '/rest/v1/rpc/save_course_structured_form_structure',
      {
        method:
          'POST',

        headers:
          apiHeaders({
            'Content-Type':
              'application/json'
          }),

        body:
          JSON.stringify({
            p_cms_token:
              cmsToken,

            p_course_assignment_id:
              courseAssignmentId,

            p_sections:
              normaliseCourseForSave(
                sections
              )
          })
      }
    );

  const result =
    await readResponse(
      response,
      'Saving Structured Form'
    );

  if (result?.ok === false) {
    throw new Error(
      result.message ||
      'Could not save the Structured Form.'
    );
  }

  return result;
}


function newSection() {
  return {
    title: '',
    description: '',
    fields: []
  };
}


function newField() {
  return {
    label: '',
    prompt: '',
    field_type: 'long_text',
    required: true,
    options: [],
    word_guidance_min: '',
    word_guidance_max: ''
  };
}


function structuredHasOtherChoice(
  options
) {
  return (
    Array.isArray(options)
      ? options
      : []
  ).some(
    option =>
      String(option || '')
        .trim()
        .toLowerCase() ===
      'other'
  );
}


function structuredSetOtherChoice(
  options,
  enabled
) {
  const ordinary =
    (
      Array.isArray(options)
        ? options
        : []
    ).filter(
      option =>
        String(option || '')
          .trim()
          .toLowerCase() !==
        'other'
    );

  return enabled
    ? [
        ...ordinary,
        'Other'
      ]
    : ordinary;
}


// GENERAL_STRUCTURED_SPECIAL_CHOICES_V1

function structuredHasNotApplicableChoice(
  options
) {
  return (
    Array.isArray(options)
      ? options
      : []
  ).some(
    option =>
      String(option || '')
        .trim()
        .toLowerCase() ===
      'not applicable'
  );
}


function structuredSetNotApplicableChoice(
  options,
  enabled
) {
  const ordinary =
    (
      Array.isArray(options)
        ? options
        : []
    ).filter(
      option =>
        String(option || '')
          .trim()
          .toLowerCase() !==
        'not applicable'
    );

  return enabled
    ? [
        ...ordinary,
        'Not Applicable'
      ]
    : ordinary;
}


function moveItem(
  items,
  index,
  direction
) {
  const next =
    [...items];

  const target =
    index + direction;

  if (
    target < 0 ||
    target >= next.length
  ) {
    return next;
  }

  [
    next[index],
    next[target]
  ] = [
    next[target],
    next[index]
  ];

  return next;
}


function normaliseForSave(
  sections
) {
  return sections.map(
    (section, sectionIndex) => ({
      title:
        String(
          section.title || ''
        ).trim(),

      description:
        section.description || '',

      section_order:
        sectionIndex + 1,

      fields:
        (
          Array.isArray(
            section.fields
          )
            ? section.fields
            : []
        ).map(
          (
            field,
            fieldIndex
          ) => ({
            label:
              String(
                field.label || ''
              ).trim(),

            prompt:
              field.prompt || '',

            field_type:
              field.field_type ||
              'long_text',

            required:
              field.required !== false,

            options:
              Array.isArray(
                field.options
              )
                ? field.options
                    .map(option =>
                      String(
                        option || ''
                      ).trim()
                    )
                    .filter(Boolean)
                : [],

            field_order:
              fieldIndex + 1,

            word_guidance_min:
              field
                .word_guidance_min ===
                '' ||
              field
                .word_guidance_min ===
                null ||
              field
                .word_guidance_min ===
                undefined
                ? null
                : Number(
                    field
                      .word_guidance_min
                  ),

            word_guidance_max:
              field
                .word_guidance_max ===
                '' ||
              field
                .word_guidance_max ===
                null ||
              field
                .word_guidance_max ===
                undefined
                ? null
                : Number(
                    field
                      .word_guidance_max
                  )
          })
        )
    })
  );
}


function validateForm(
  sections
) {
  for (
    let sectionIndex = 0;
    sectionIndex <
      sections.length;
    sectionIndex += 1
  ) {
    const section =
      sections[sectionIndex];

    if (
      !String(
        section.title || ''
      ).trim()
    ) {
      return (
        `Section ${
          sectionIndex + 1
        } needs a title.`
      );
    }

    const fields =
      Array.isArray(
        section.fields
      )
        ? section.fields
        : [];

    for (
      let fieldIndex = 0;
      fieldIndex <
        fields.length;
      fieldIndex += 1
    ) {
      const field =
        fields[fieldIndex];

      if (
        !String(
          field.label || ''
        ).trim()
      ) {
        return (
          `Section ${
            sectionIndex + 1
          }, question ${
            fieldIndex + 1
          } needs a label.`
        );
      }

      if (
        [
          'single_choice',
          'multiple_choice'
        ].includes(
          field.field_type
        )
      ) {
        const options =
          Array.isArray(
            field.options
          )
            ? field.options
                .map(option =>
                  String(
                    option || ''
                  ).trim()
                )
                .filter(Boolean)
            : [];

        if (
          options.length < 2
        ) {
          return (
            `"${field.label}" needs at least two choices.`
          );
        }
      }

      const min =
        field.word_guidance_min ===
          '' ||
        field.word_guidance_min ===
          null ||
        field.word_guidance_min ===
          undefined
          ? null
          : Number(
              field
                .word_guidance_min
            );

      const max =
        field.word_guidance_max ===
          '' ||
        field.word_guidance_max ===
          null ||
        field.word_guidance_max ===
          undefined
          ? null
          : Number(
              field
                .word_guidance_max
            );

      if (
        min !== null &&
        (
          !Number.isFinite(min) ||
          min < 0
        )
      ) {
        return (
          `"${field.label}" has an invalid minimum word guide.`
        );
      }

      if (
        max !== null &&
        (
          !Number.isFinite(max) ||
          max < 0
        )
      ) {
        return (
          `"${field.label}" has an invalid maximum word guide.`
        );
      }

      if (
        min !== null &&
        max !== null &&
        max < min
      ) {
        return (
          `"${field.label}" has a maximum word guide below its minimum.`
        );
      }
    }
  }

  return '';
}


export default function StructuredFormBuilder({
  mode = 'library',
  type,
  assignment,
  label,
  session,
  canEdit: requestedCanEdit = true
}) {
  const isCourse =
    mode === 'course';

  const sourceKey =
    isCourse
      ? assignment?.id
      : type?.id;

  const canEdit =
    isCourse
      ? true
      : requestedCanEdit;
  const [
    sections,
    setSections
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    success,
    setSuccess
  ] = useState('');


  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');
    setSuccess('');

    (
      isCourse
        ? loadCourseStructuredForm(
            sourceKey
          )
        : loadStructuredForm(
            sourceKey
          )
    )
      .then(result => {
        if (!cancelled) {
          setSections(
            Array.isArray(result)
              ? result
              : []
          );
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(
            err?.message ||
              'Could not load the Structured Form.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    isCourse,
    sourceKey
  ]);


  function updateSection(
    sectionIndex,
    patch
  ) {
    setSections(prev =>
      prev.map(
        (section, index) =>
          index ===
          sectionIndex
            ? {
                ...section,
                ...patch
              }
            : section
      )
    );
  }


  function updateField(
    sectionIndex,
    fieldIndex,
    patch
  ) {
    setSections(prev =>
      prev.map(
        (
          section,
          currentSectionIndex
        ) => {
          if (
            currentSectionIndex !==
            sectionIndex
          ) {
            return section;
          }

          const fields =
            Array.isArray(
              section.fields
            )
              ? section.fields
              : [];

          return {
            ...section,
            fields:
              fields.map(
                (
                  field,
                  currentFieldIndex
                ) =>
                  currentFieldIndex ===
                  fieldIndex
                    ? {
                        ...field,
                        ...patch
                      }
                    : field
              )
          };
        }
      )
    );
  }


  function addQuestion(
    sectionIndex
  ) {
    setSections(prev =>
      prev.map(
        (section, index) =>
          index ===
          sectionIndex
            ? {
                ...section,
                fields: [
                  ...(
                    Array.isArray(
                      section.fields
                    )
                      ? section.fields
                      : []
                  ),
                  newField()
                ]
              }
            : section
      )
    );
  }


  function deleteQuestion(
    sectionIndex,
    fieldIndex
  ) {
    setSections(prev =>
      prev.map(
        (section, index) =>
          index ===
          sectionIndex
            ? {
                ...section,
                fields:
                  (
                    section.fields ||
                    []
                  ).filter(
                    (
                      _,
                      currentIndex
                    ) =>
                      currentIndex !==
                      fieldIndex
                  )
              }
            : section
      )
    );
  }


  function moveQuestion(
    sectionIndex,
    fieldIndex,
    direction
  ) {
    setSections(prev =>
      prev.map(
        (section, index) =>
          index ===
          sectionIndex
            ? {
                ...section,
                fields:
                  moveItem(
                    section.fields ||
                      [],
                    fieldIndex,
                    direction
                  )
              }
            : section
      )
    );
  }


  async function handleSave() {
    if (!canEdit) {
      return;
    }

    const validation =
      validateForm(
        sections
      );

    if (validation) {
      setError(validation);
      setSuccess('');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (isCourse) {
        await saveCourseStructuredForm(
          sourceKey,
          sections,
          session.cmsToken
        );
      } else {
        const payload =
          normaliseForSave(
            sections
          );

        await saveStructuredForm(
          sourceKey,
          payload,
          session.cmsToken
        );
      }

      const refreshed =
        isCourse
          ? await loadCourseStructuredForm(
              sourceKey
            )
          : await loadStructuredForm(
              sourceKey
            );

      setSections(
        Array.isArray(refreshed)
          ? refreshed
          : []
      );

      setSuccess(
        isCourse
          ? 'Form structure saved.'
          : 'Structured Form saved.'
      );
    } catch (err) {
      setError(
        err?.message ||
          'Could not save the Structured Form.'
      );
    } finally {
      setSaving(false);
    }
  }


  if (loading) {
    return (
      <div style={styles.muted}>
        Loading form structure...
      </div>
    );
  }


  return (
    <div style={styles.wrap}>
      <div style={styles.headingRow}>
        <div>
          <h4 style={styles.heading}>
            {
              isCourse
                ? label
                : 'Structured Form'
            }
          </h4>

          <div style={styles.help}>
            {
              isCourse
                ? 'This is the course-specific copy of the Assignment Library form. Changes here do not alter the reusable Library template.'
                : 'Build the reusable form here. Submission instances, editing windows and deadlines are configured separately for each course assignment.'
            }
          </div>
        </div>

        {canEdit && (
          <button
            type="button"
            style={styles.saveBtn}
            disabled={saving}
            onClick={handleSave}
          >
            {saving
              ? 'Saving...'
              : 'Save Form'}
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {success && (
        <div style={styles.success}>
          {success}
        </div>
      )}

      {sections.length === 0 && (
        <div style={styles.empty}>
          No sections yet.
        </div>
      )}

      {sections.map(
        (
          section,
          sectionIndex
        ) => (
          <div
            key={
              section.id ||
              `section-${sectionIndex}`
            }
            style={styles.section}
          >
            <div
              style={
                styles.sectionTop
              }
            >
              <div
                style={
                  styles.sectionNumber
                }
              >
                Section {
                  sectionIndex + 1
                }
              </div>

              {canEdit && (
                <div
                  style={
                    styles.smallButtons
                  }
                >
                  <button
                    type="button"
                    style={
                      styles.miniBtn
                    }
                    disabled={
                      sectionIndex ===
                      0
                    }
                    onClick={() =>
                      setSections(
                        prev =>
                          moveItem(
                            prev,
                            sectionIndex,
                            -1
                          )
                      )
                    }
                  >
                    ↑
                  </button>

                  <button
                    type="button"
                    style={
                      styles.miniBtn
                    }
                    disabled={
                      sectionIndex ===
                      sections.length -
                        1
                    }
                    onClick={() =>
                      setSections(
                        prev =>
                          moveItem(
                            prev,
                            sectionIndex,
                            1
                          )
                      )
                    }
                  >
                    ↓
                  </button>

                  <button
                    type="button"
                    style={
                      styles.deleteBtn
                    }
                    onClick={() =>
                      setSections(
                        prev =>
                          prev.filter(
                            (
                              _,
                              index
                            ) =>
                              index !==
                              sectionIndex
                          )
                      )
                    }
                  >
                    Delete Section
                  </button>
                </div>
              )}
            </div>

            <label
              style={styles.label}
            >
              Section title
            </label>

            <input
              style={styles.input}
              value={
                section.title || ''
              }
              readOnly={!canEdit}
              onChange={event =>
                updateSection(
                  sectionIndex,
                  {
                    title:
                      event.target
                        .value
                  }
                )
              }
              placeholder="e.g. Section I: Project Foundation"
            />

            <label
              style={styles.label}
            >
              Section introduction /
              instructions
            </label>

            <textarea
              style={styles.textarea}
              rows={3}
              value={
                section.description ||
                ''
              }
              readOnly={!canEdit}
              onChange={event =>
                updateSection(
                  sectionIndex,
                  {
                    description:
                      event.target
                        .value
                  }
                )
              }
              placeholder="Optional instructions for this section"
            />

            <div
              style={
                styles.questionList
              }
            >
              {(
                section.fields ||
                []
              ).map(
                (
                  field,
                  fieldIndex
                ) => (
                  <div
                    key={
                      field.id ||
                      `field-${sectionIndex}-${fieldIndex}`
                    }
                    style={
                      styles.question
                    }
                  >
                    <div
                      style={
                        styles.questionTop
                      }
                    >
                      <strong>
                        Question {
                          fieldIndex +
                          1
                        }
                      </strong>

                      {canEdit && (
                        <div
                          style={
                            styles.smallButtons
                          }
                        >
                          <button
                            type="button"
                            style={
                              styles.miniBtn
                            }
                            disabled={
                              fieldIndex ===
                              0
                            }
                            onClick={() =>
                              moveQuestion(
                                sectionIndex,
                                fieldIndex,
                                -1
                              )
                            }
                          >
                            ↑
                          </button>

                          <button
                            type="button"
                            style={
                              styles.miniBtn
                            }
                            disabled={
                              fieldIndex ===
                              (
                                section
                                  .fields ||
                                []
                              )
                                .length -
                              1
                            }
                            onClick={() =>
                              moveQuestion(
                                sectionIndex,
                                fieldIndex,
                                1
                              )
                            }
                          >
                            ↓
                          </button>

                          <button
                            type="button"
                            style={
                              styles.deleteBtn
                            }
                            onClick={() =>
                              deleteQuestion(
                                sectionIndex,
                                fieldIndex
                              )
                            }
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>

                    <div
                      style={
                        styles.grid
                      }
                    >
                      <div>
                        <label
                          style={
                            styles.label
                          }
                        >
                          Label
                        </label>

                        <input
                          style={
                            styles.input
                          }
                          value={
                            field.label ||
                            ''
                          }
                          readOnly={
                            !canEdit
                          }
                          onChange={
                            event =>
                              updateField(
                                sectionIndex,
                                fieldIndex,
                                {
                                  label:
                                    event
                                      .target
                                      .value
                                }
                              )
                          }
                          placeholder="e.g. A. Core Research Question"
                        />
                      </div>

                      <div>
                        <label
                          style={
                            styles.label
                          }
                        >
                          Response type
                        </label>

                        <select
                          style={
                            styles.input
                          }
                          value={
                            field
                              .field_type ||
                            'long_text'
                          }
                          disabled={
                            !canEdit
                          }
                          onChange={
                            event =>
                              updateField(
                                sectionIndex,
                                fieldIndex,
                                {
                                  field_type:
                                    event
                                      .target
                                      .value,
                                  options:
                                    [
                                      'single_choice',
                                      'multiple_choice'
                                    ].includes(
                                      event
                                        .target
                                        .value
                                    )
                                      ? (
                                          field
                                            .options ||
                                          []
                                        )
                                      : []
                                }
                              )
                          }
                        >
                          <option
                            value="short_text"
                          >
                            Short text
                          </option>

                          <option
                            value="long_text"
                          >
                            Long text
                          </option>

                          <option
                            value="single_choice"
                          >
                            Single choice
                          </option>

                          <option
                            value="multiple_choice"
                          >
                            Multiple choice
                          </option>
                        </select>
                      </div>
                    </div>

                    <label
                      style={
                        styles.label
                      }
                    >
                      Prompt /
                      instructions
                    </label>

                    <textarea
                      style={
                        styles.textarea
                      }
                      rows={3}
                      value={
                        field.prompt ||
                        ''
                      }
                      readOnly={
                        !canEdit
                      }
                      onChange={
                        event =>
                          updateField(
                            sectionIndex,
                            fieldIndex,
                            {
                              prompt:
                                event
                                  .target
                                  .value
                            }
                          )
                      }
                      placeholder="What should the student do here?"
                    />

                    {[
                      'single_choice',
                      'multiple_choice'
                    ].includes(
                      field.field_type
                    ) && (
                      <>
                        <label
                          style={
                            styles.label
                          }
                        >
                          Choices
                          (one per
                          line)
                        </label>

                        <textarea
                          style={
                            styles.textarea
                          }
                          rows={4}
                          value={
                            (
                              field.options ||
                              []
                            ).join(
                              '\n'
                            )
                          }
                          readOnly={
                            !canEdit
                          }
                          onChange={
                            event =>
                              updateField(
                                sectionIndex,
                                fieldIndex,
                                {
                                  options:
                                    event
                                      .target
                                      .value
                                      .split(
                                        '\n'
                                      )
                                }
                              )
                          }
                          placeholder={
                            'Humanities\nSocial Sciences\nNatural Sciences'
                          }
                        />
                      </>
                    )}

                    {[
                      'single_choice',
                      'multiple_choice'
                    ].includes(
                      field.field_type
                    ) && (
                      <div
                        style={{
                          display: 'grid',
                          gap: '12px',
                          marginTop: '10px'
                        }}
                      >

                        <div>
                          <label
                            style={
                              styles.checkbox
                            }
                          >
                            <input
                              type="checkbox"
                              checked={
                                structuredHasOtherChoice(
                                  field.options
                                )
                              }
                              disabled={
                                !canEdit
                              }
                              onChange={
                                event =>
                                  updateField(
                                    sectionIndex,
                                    fieldIndex,
                                    {
                                      options:
                                        structuredSetOtherChoice(
                                          field.options,
                                          event
                                            .target
                                            .checked
                                        )
                                    }
                                  )
                              }
                            />

                            Allow “Other”
                            with optional text
                          </label>

                          <div
                            style={{
                              color:
                                '#66788a',
                              fontSize:
                                '11px',
                              marginTop:
                                '4px',
                              marginLeft:
                                '24px'
                            }}
                          >
                            Students who select
                            Other can optionally
                            specify their response.
                          </div>
                        </div>


                        <div>
                          <label
                            style={
                              styles.checkbox
                            }
                          >
                            <input
                              type="checkbox"
                              checked={
                                structuredHasNotApplicableChoice(
                                  field.options
                                )
                              }
                              disabled={
                                !canEdit
                              }
                              onChange={
                                event =>
                                  updateField(
                                    sectionIndex,
                                    fieldIndex,
                                    {
                                      options:
                                        structuredSetNotApplicableChoice(
                                          field.options,
                                          event
                                            .target
                                            .checked
                                        )
                                    }
                                  )
                              }
                            />

                            Allow “Not Applicable”
                            with optional explanation
                          </label>

                          <div
                            style={{
                              color:
                                '#66788a',
                              fontSize:
                                '11px',
                              marginTop:
                                '4px',
                              marginLeft:
                                '24px'
                            }}
                          >
                            Students who select
                            Not Applicable can
                            optionally explain why.
                            In multiple-choice
                            questions, Not Applicable
                            cannot be combined with
                            another choice.
                          </div>
                        </div>

                      </div>
                    )}

                    <div
                      style={
                        styles.bottomRow
                      }
                    >
                      <label
                        style={
                          styles.checkbox
                        }
                      >
                        <input
                          type="checkbox"
                          checked={
                            field.required !==
                            false
                          }
                          disabled={
                            !canEdit
                          }
                          onChange={
                            event =>
                              updateField(
                                sectionIndex,
                                fieldIndex,
                                {
                                  required:
                                    event
                                      .target
                                      .checked
                                }
                              )
                          }
                        />
                        Required
                      </label>

                      <div
                        style={{
                          ...styles.wordGuide,
                          display:
                            [
                              'short_text',
                              'long_text'
                            ].includes(
                              field.field_type
                            )
                              ? undefined
                              : 'none'
                        }}
                      >
                        <span>
                          Word limit:
                        </span>

                        <input
                          style={
                            styles.smallInput
                          }
                          type="number"
                          min="0"
                          placeholder="Min"
                          value={
                            field
                              .word_guidance_min ??
                            ''
                          }
                          readOnly={
                            !canEdit
                          }
                          onChange={
                            event =>
                              updateField(
                                sectionIndex,
                                fieldIndex,
                                {
                                  word_guidance_min:
                                    event
                                      .target
                                      .value
                                }
                              )
                          }
                        />

                        <span>
                          –
                        </span>

                        <input
                          style={
                            styles.smallInput
                          }
                          type="number"
                          min="0"
                          placeholder="Max"
                          value={
                            field
                              .word_guidance_max ??
                            ''
                          }
                          readOnly={
                            !canEdit
                          }
                          onChange={
                            event =>
                              updateField(
                                sectionIndex,
                                fieldIndex,
                                {
                                  word_guidance_max:
                                    event
                                      .target
                                      .value
                                }
                              )
                          }
                        />
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>

            {canEdit && (
              <button
                type="button"
                style={
                  styles.addQuestionBtn
                }
                onClick={() =>
                  addQuestion(
                    sectionIndex
                  )
                }
              >
                + Add Question
              </button>
            )}
          </div>
        )
      )}

      {canEdit && (
        <div style={styles.footer}>
          <button
            type="button"
            style={styles.addSectionBtn}
            onClick={() =>
              setSections(prev => [
                ...prev,
                newSection()
              ])
            }
          >
            + Add Section
          </button>

          <button
            type="button"
            style={styles.saveBtn}
            disabled={saving}
            onClick={handleSave}
          >
            {saving
              ? 'Saving...'
              : 'Save Form'}
          </button>
        </div>
      )}

      {!canEdit && (
        <div style={styles.muted}>
          View only — only the
          instructor who created this
          assignment type can change
          its form.
        </div>
      )}
    </div>
  );
}


const styles = {
  wrap: {
    borderTop:
      '1px solid #d7dee5',
    padding:
      '18px 20px 22px',
    background:
      '#f8fafb'
  },

  headingRow: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems:
      'flex-start',
    gap: '16px',
    marginBottom: '16px'
  },

  heading: {
    margin: '0 0 4px',
    fontSize: '16px',
    color: '#1f2933'
  },

  help: {
    color: '#66788a',
    fontSize: '13px',
    lineHeight: 1.45
  },

  muted: {
    color: '#66788a',
    fontSize: '13px'
  },

  empty: {
    padding: '18px',
    border:
      '1px dashed #cbd5de',
    borderRadius: '8px',
    color: '#66788a',
    textAlign: 'center'
  },

  error: {
    background: '#faeaea',
    border:
      '1px solid #e8bcbc',
    color: '#a12a2a',
    borderRadius: '8px',
    padding: '9px 12px',
    marginBottom: '12px',
    fontSize: '13px'
  },

  success: {
    background: '#e7f5ed',
    border:
      '1px solid #b9dfc9',
    color: '#216e45',
    borderRadius: '8px',
    padding: '9px 12px',
    marginBottom: '12px',
    fontSize: '13px'
  },

  section: {
    border:
      '1px solid #ccd6df',
    borderRadius: '10px',
    padding: '16px',
    background: 'white',
    marginBottom: '14px'
  },

  sectionTop: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },

  sectionNumber: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#215b7d'
  },

  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 700,
    color: '#42515f',
    margin:
      '10px 0 5px'
  },

  input: {
    width: '100%',
    boxSizing:
      'border-box',
    border:
      '1px solid #cbd5de',
    borderRadius: '7px',
    padding: '9px 10px',
    fontSize: '13px',
    background: 'white'
  },

  textarea: {
    width: '100%',
    boxSizing:
      'border-box',
    border:
      '1px solid #cbd5de',
    borderRadius: '7px',
    padding: '9px 10px',
    fontSize: '13px',
    fontFamily: 'inherit',
    resize: 'vertical'
  },

  questionList: {
    marginTop: '16px',
    display: 'grid',
    gap: '12px'
  },

  question: {
    border:
      '1px solid #e0e6eb',
    borderRadius: '8px',
    padding: '14px',
    background: '#fbfcfd'
  },

  questionTop: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13px',
    color: '#2f3f4d'
  },

  smallButtons: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap'
  },

  miniBtn: {
    border:
      '1px solid #cbd5de',
    background: 'white',
    borderRadius: '6px',
    padding: '4px 8px',
    cursor: 'pointer'
  },

  deleteBtn: {
    border:
      '1px solid #e1bcbc',
    background: '#fff7f7',
    color: '#9b3434',
    borderRadius: '6px',
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: '12px'
  },

  grid: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(0, 2fr) minmax(150px, 1fr)',
    gap: '12px'
  },

  bottomRow: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    marginTop: '12px'
  },

  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    color: '#42515f',
    fontSize: '13px'
  },

  wordGuide: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#66788a',
    fontSize: '12px'
  },

  smallInput: {
    width: '72px',
    border:
      '1px solid #cbd5de',
    borderRadius: '6px',
    padding: '6px 7px',
    fontSize: '12px'
  },

  addQuestionBtn: {
    marginTop: '12px',
    border:
      '1px solid #8fb2c7',
    background: '#f4f9fc',
    color: '#215b7d',
    borderRadius: '7px',
    padding: '7px 10px',
    fontWeight: 600,
    cursor: 'pointer'
  },

  footer: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    gap: '12px',
    marginTop: '14px'
  },

  addSectionBtn: {
    border:
      '1px solid #8fb2c7',
    background: 'white',
    color: '#215b7d',
    borderRadius: '7px',
    padding: '9px 13px',
    fontWeight: 600,
    cursor: 'pointer'
  },

  saveBtn: {
    border: 'none',
    background: '#215b7d',
    color: 'white',
    borderRadius: '7px',
    padding: '9px 14px',
    fontWeight: 700,
    cursor: 'pointer'
  }
};
